import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ProduktionsmalBasdataDTO } from "~app/common/models/produktionsmal/ProduktionsmalBasdataDTO";
import { ProduktionsmalResponseDTO } from "~app/common/models/produktionsmal/ProduktionsmalResponseDTO";
import { HandelssortimentsgruppDTO } from "~app/common/models/common/HandelssortimentsgruppDTO";
import { MarknadsomradeDTO } from "~app/common/models/common/MarknadsomradeDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IHandelssortimentsgruppHandelssortimentFilter, IOmradeFilter, IProduktionsmalRequest } from "../produktionsmal.models";

@Injectable({
    providedIn: "root"
})
export class ProduktionsmalService {
    private readonly apiClient = inject(ApiClientService);

    private readonly handelssortimentType = {
        group: "HG",
        handelssortiment: "HS"
    };

    private readonly selectedLevel = {
        none: "none",
        partial: "partial",
        all: "all"
    };

    getProduktionsmal(request: IProduktionsmalRequest): Observable<ProduktionsmalResponseDTO> {
        return this.apiClient.post(`planera/GetProduktionsmal`, request, true);
    }

    getProduktionsmalBasdata(): Observable<ProduktionsmalBasdataDTO> {
        return this.apiClient.get<ProduktionsmalBasdataDTO>("planera/GetProduktionsmalBasdata", true);
    }

    // --------------------------------- Tree Selectors --------------------------------- //

    setupHandelssortimentTreeSelector(handelssortiment: HandelssortimentsgruppDTO[], valdaHs: IHandelssortimentsgruppHandelssortimentFilter[]): SelectionItem[] {
        const response: SelectionItem[] = [];

        handelssortiment.forEach(hs => {
            response.push(this.createTreeSelectionNode(hs, valdaHs));
        });

        this.setPartialSelected(response);

        return response;
    }

    setupOmradeTreeSelector(omraden: MarknadsomradeDTO[], valdaOmraden: IOmradeFilter[]): SelectionItem[] {
        const response: SelectionItem[] = [];

        response.push(...omraden.map(marknadsOmrade => ({
            id: marknadsOmrade.id,
            text: marknadsOmrade.namn,
            selected: valdaOmraden?.some(p => p.id === marknadsOmrade.id && p.typ === "MO"),
            children: marknadsOmrade.children.map(resultatOmrade => (({
                id: resultatOmrade.id,
                text: resultatOmrade.namn,
                selected: valdaOmraden?.some(p => p.id === resultatOmrade.id && p.typ === "RO"),

                children: resultatOmrade.children.map(produktionsOmrade => (({
                    id: produktionsOmrade.id,
                    text: produktionsOmrade.namn,

                    // eslint-disable-next-line sonarjs/no-nested-functions
                    selected: valdaOmraden?.some(p => p.id === produktionsOmrade.id && p.typ === "PO")
                }) as SelectionItem))
            }) as SelectionItem))
        } as SelectionItem)));

        this.setPartialSelected(response);

        return response;
    }

    getSelectedHandelssortiment(handelssortimentsgrupper: SelectionItem[]) {
        const data = [];
        handelssortimentsgrupper.forEach(hsg => {
            if (hsg.selected) {
                data.push( {
                    typ: this.handelssortimentType.group,
                    id: hsg.value
                });
            }

            if (hsg.selected || hsg.partialSelected) {
                hsg.children?.forEach(hs => {
                    if (hs.selected) {
                        data.push( {
                            typ: this.handelssortimentType.handelssortiment,
                            id: hs.value
                        });
                    }
                });
            }
        });

        return data;
    }

    getSelectedOmraden(omraden: SelectionItem[]): IOmradeFilter[] {
        const data: IOmradeFilter[] = [];

        omraden.forEach(mo => {
            if (mo.selected) {
                data.push({ id: mo.id,
                    typ: "MO" });
            }
            mo.children.forEach(ro => {
                if (ro.selected) {
                    data.push({ id: ro.id,
                        typ: "RO" });
                }
                ro.children.forEach(po => {
                    if (po.selected) {
                        data.push({ id: po.id,
                            typ: "PO" });
                    }
                });
            });
        });

        return data;
    }

    private createTreeSelectionNode(node: HandelssortimentsgruppDTO, valdaHs: IHandelssortimentsgruppHandelssortimentFilter[]) {
        let children: SelectionItem[] = null;
        if (!ObjectUtils.isNullOrUndefined(node.children) && node.children.length > 0) {
            children = children === null ? [] : children;
            node.children.forEach((data: any) => {
                children.push(this.createTreeSelectionNode(data, valdaHs));
            });
        }

        return {
            value: node.id,
            text: node.namn,
            selected: this.setNodeSelection(node, valdaHs),
            partialSelected: false,
            children: children
        } as SelectionItem;
    }

    private setNodeSelection(node: HandelssortimentsgruppDTO, valdaHs: IHandelssortimentsgruppHandelssortimentFilter[]): boolean {
        let res = false;
        try {
            valdaHs?.forEach((grupp) => {
                if (grupp.id === node.id &&
                    (grupp.typ === this.handelssortimentType.group && !ObjectUtils.isNullOrUndefined(node.children) ||
                    grupp.typ === this.handelssortimentType.handelssortiment && ObjectUtils.isNullOrUndefined(node.children))) {
                    res = true;
                }
            });

        } catch (err) {
            return res;
        }

        return res;
    }

    // ---------------------- Partial Selection Setting ---------------------- //

    private setPartialSelected(array: SelectionItem[]) {
        array.forEach(a => {
            if (!ObjectUtils.isNullOrUndefined(a.children)) {
                const res = this.setSelectedChildren(a.children);
                if (res === this.selectedLevel.all) {
                    a.selected = true;
                } else if (res ===  this.selectedLevel.partial) {
                    a.partialSelected = true;
                }
            }
        });
    }

    private setSelectedChildren(array: SelectionItem[]): string {
        let numberOfSelected = 0;
        let isPartial = false;
        array.forEach(a => {
            if (!a.selected && !ObjectUtils.isNullOrUndefined(a.children)) {
                const res = this.setSelectedChildren(a.children);
                if (res === this.selectedLevel.all) {
                    a.selected = true;
                } else if (res === this.selectedLevel.partial) {
                    a.partialSelected = true;
                    isPartial = true;
                }
            } else if (a.selected === true) {
                numberOfSelected++;
            }
        });
        if (numberOfSelected === 0 && !isPartial) {
            return this.selectedLevel.none;
        } else if (numberOfSelected === array.length) {
            return this.selectedLevel.all;
        } else if (isPartial || (numberOfSelected < array.length && numberOfSelected > 0)) {
            return this.selectedLevel.partial;
        }

        return this.selectedLevel.none;
    }
}
